import { useCallback, useState } from 'react';
import {
  migrationApi,
  type MigrationContractAdjustmentPayload,
  type MigrationCaseDto,
  type MigrationCaseDetailsResponse,
  type MigrationCaseSearchParams,
  type MigrationWizardChecks,
  type MigrationWizardStepData,
} from '@/lib/api';

type UseMigrationCasesOptions = {
  limit?: number;
};

export function useMigrationCases(options?: UseMigrationCasesOptions) {
  const limit = options?.limit ?? 200;
  const [cases, setCases] = useState<MigrationCaseDto[]>([]);
  const [isLoadingCases, setIsLoadingCases] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCases = useCallback(async (standortId: number, status?: string) => {
    setError(null);
    setIsLoadingCases(true);
    try {
      const items = await migrationApi.listCases({ standort_id: standortId, status, limit, offset: 0 });
      setCases(items);
      return items;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Migration-Cases konnten nicht geladen werden';
      setError(message);
      throw err;
    } finally {
      setIsLoadingCases(false);
    }
  }, [limit]);

  const searchCases = useCallback(async (params: MigrationCaseSearchParams) => {
    setError(null);
    setIsLoadingCases(true);
    try {
      const items = await migrationApi.searchCases({ ...params, limit: params.limit ?? limit });
      setCases(items);
      return items;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Migration-Cases konnten nicht geladen werden';
      setError(message);
      throw err;
    } finally {
      setIsLoadingCases(false);
    }
  }, [limit]);

  const sendCaseEmail = useCallback(async (caseId: number, resend = false) => {
    setError(null);
    try {
      const result = resend ? await migrationApi.resendCaseEmail(caseId) : await migrationApi.sendCaseEmail(caseId);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Case-Mail konnte nicht versendet werden';
      setError(message);
      throw err;
    }
  }, []);

  const confirmCaseMigration = useCallback(async (caseId: number, payload?: Record<string, unknown>) => {
    setError(null);
    try {
      const result = await migrationApi.confirmCaseMigration(caseId, payload);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Case konnte nicht migriert werden';
      setError(message);
      throw err;
    }
  }, []);

  const getCaseDetails = useCallback(async (caseId: number): Promise<MigrationCaseDetailsResponse> => {
    setError(null);
    try {
      const result = await migrationApi.getCaseDetails(caseId);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Case-Details konnten nicht geladen werden';
      setError(message);
      throw err;
    }
  }, []);

  const checkCaseWizard = useCallback(async (caseId: number, stepData?: MigrationWizardStepData): Promise<MigrationWizardChecks> => {
    setError(null);
    try {
      const result = await migrationApi.checkCaseWizard(caseId, stepData ? { step_data: stepData } : undefined);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Wizard-Checks konnten nicht ausgeführt werden';
      setError(message);
      throw err;
    }
  }, []);

  const saveCaseWizardDraft = useCallback(async (caseId: number, stepData: MigrationWizardStepData, notes?: string | null) => {
    setError(null);
    try {
      const result = await migrationApi.saveCaseWizardDraft(caseId, { step_data: stepData, notes: notes ?? null });
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Wizard-Entwurf konnte nicht gespeichert werden';
      setError(message);
      throw err;
    }
  }, []);

  const previewCaseWizardConfirm = useCallback(async (caseId: number, stepData?: MigrationWizardStepData) => {
    setError(null);
    try {
      const result = await migrationApi.previewCaseWizardConfirm(caseId, stepData ? { step_data: stepData } : undefined);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Wizard-Vorschau konnte nicht geladen werden';
      setError(message);
      throw err;
    }
  }, []);

  const getOpenSummary = useCallback(async (standortId: number) => {
    setError(null);
    try {
      return await migrationApi.getOpenSummary(standortId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Open-Summary konnte nicht geladen werden';
      setError(message);
      throw err;
    }
  }, []);

  const setFavorite = useCallback(async (caseId: number) => {
    setError(null);
    try {
      await migrationApi.setFavorite(caseId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Favorit konnte nicht gesetzt werden';
      setError(message);
      throw err;
    }
  }, []);

  const removeFavorite = useCallback(async (caseId: number) => {
    setError(null);
    try {
      await migrationApi.removeFavorite(caseId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Favorit konnte nicht entfernt werden';
      setError(message);
      throw err;
    }
  }, []);

  const listFavorites = useCallback(async (standortId: number) => {
    setError(null);
    try {
      return await migrationApi.listFavorites(standortId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Favoriten konnten nicht geladen werden';
      setError(message);
      throw err;
    }
  }, []);

  const getCaseLinks = useCallback(async (caseId: number) => {
    setError(null);
    try {
      return await migrationApi.getCaseLinks(caseId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Links konnten nicht geladen werden';
      setError(message);
      throw err;
    }
  }, []);

  const upsertCaseLink = useCallback(async (caseId: number, linkType: string, payload: Record<string, unknown>) => {
    setError(null);
    try {
      return await migrationApi.upsertCaseLink(caseId, linkType, payload);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Link konnte nicht gespeichert werden';
      setError(message);
      throw err;
    }
  }, []);

  const previewContractAdjustment = useCallback(async (caseId: number, payload: MigrationContractAdjustmentPayload) => {
    setError(null);
    try {
      return await migrationApi.previewContractAdjustment(caseId, payload);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Preview fehlgeschlagen';
      setError(message);
      throw err;
    }
  }, []);

  const executeContractAdjustment = useCallback(async (caseId: number, payload: MigrationContractAdjustmentPayload) => {
    setError(null);
    try {
      return await migrationApi.executeContractAdjustment(caseId, payload);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ausführung fehlgeschlagen';
      setError(message);
      throw err;
    }
  }, []);

  const previewContractTermination = useCallback(async (caseId: number, payload: Record<string, unknown>) => {
    setError(null);
    try {
      return await migrationApi.previewContractTermination(caseId, payload);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Preview fehlgeschlagen';
      setError(message);
      throw err;
    }
  }, []);

  const executeContractTermination = useCallback(async (caseId: number, payload: Record<string, unknown>) => {
    setError(null);
    try {
      return await migrationApi.executeContractTermination(caseId, payload);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ausführung fehlgeschlagen';
      setError(message);
      throw err;
    }
  }, []);

  const previewOrderAdjustment = useCallback(async (caseId: number, payload: Record<string, unknown>) => {
    setError(null);
    try {
      return await migrationApi.previewOrderAdjustment(caseId, payload);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Preview fehlgeschlagen';
      setError(message);
      throw err;
    }
  }, []);

  const executeOrderAdjustment = useCallback(async (caseId: number, payload: Record<string, unknown>) => {
    setError(null);
    try {
      return await migrationApi.executeOrderAdjustment(caseId, payload);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ausführung fehlgeschlagen';
      setError(message);
      throw err;
    }
  }, []);

  return {
    cases,
    isLoadingCases,
    error,
    loadCases,
    searchCases,
    getOpenSummary,
    sendCaseEmail,
    setFavorite,
    removeFavorite,
    listFavorites,
    getCaseLinks,
    upsertCaseLink,
    previewContractAdjustment,
    executeContractAdjustment,
    previewContractTermination,
    executeContractTermination,
    previewOrderAdjustment,
    executeOrderAdjustment,
    confirmCaseMigration,
    getCaseDetails,
    checkCaseWizard,
    saveCaseWizardDraft,
    previewCaseWizardConfirm,
  };
}
